/**
 * 헌혈요청 / 커뮤니티 / 공지 디자인 확인용 더미 데이터.
 * NEXT_PUBLIC_CONTENT_MOCK=true 일 때 services/community.ts, services/notice.ts가 이 구현으로 위임한다.
 *
 * 신규 백엔드 필드(region, deadlineAt, hospitalName, requiredCount, category, urgent)가
 * 채워진 상태를 보여주기 위한 것 — 운영 데이터엔 아직 값이 없다.
 */

const isoIn = (hours: number) =>
  new Date(Date.now() + hours * 3600 * 1000).toISOString();
const isoAgo = (minutes: number) =>
  new Date(Date.now() - minutes * 60000).toISOString();

const writer = (
  displayName: string,
  uuid: string,
  bloodDonationCount = 0
) => ({
  uuid,
  accountUuid: uuid,
  displayName,
  profileImageUrl: '',
  bloodDonationCount,
  pplez: false,
  fcmToken: '',
});

/** 상세 화면 댓글(Comment 컴포넌트가 community.replies로 받는다) */
const reply = (
  uuid: string,
  name: string,
  writerUuid: string,
  content: string,
  minAgo: number
) => ({
  uuid,
  content,
  createdAt: isoAgo(minAgo),
  reportCount: 0,
  writer: writer(name, writerUuid),
  nestedReplies: [],
});

const patient = (abo: string, rh: 'POSITIVE' | 'NEGATIVE') => ({
  uuid: `patient-${abo}-${rh}`,
  createdAt: isoAgo(1000),
  modifiedAt: isoAgo(1000),
  bloodType: { abo, rh },
  status: 'ACTIVE',
});

// ===== 헌혈요청 =====
const DONATIONS = [
  {
    uuid: 'don-1',
    title: '서울성모병원 O형 혈소판 3팩 급구합니다',
    content:
      '어머니 수술이 8월 14일 오전으로 잡혔습니다. 지정헌혈 가능하신 분 연락 부탁드려요.\n헌혈증서는 바로 전달해 드리겠습니다.',
    bloodProduct: 'PLATELET',
    patient: patient('O', 'POSITIVE'),
    writer: writer('김하늘', 'w-1', 3),
    images: [],
    hits: 248,
    viewsCount: 248,
    likesCount: 31,
    repliesCount: 12,
    lettersCount: 0,
    liked: false,
    liker: [],
    status: 'ACTIVE',
    createdAt: isoAgo(10),
    lastRenewedAt: isoAgo(10),
    modifiedAt: isoAgo(10),
    renewCount: 0,
    // 신규 필드
    hospitalName: '서울성모병원 헌혈실',
    hospitalAddress: '서울 서초구 반포대로 222',
    region: '서울 서초구',
    deadlineAt: isoIn(20),
    requiredCount: 3,
    fulfilledCount: 2,
    replies: [
      reply(
        'rep-1',
        '이준서',
        'w-2',
        '지정헌혈 가능합니다. 내일 오전에 방문할게요!',
        35
      ),
      reply('rep-2', '최유진', 'w-4', '혈소판도 지정헌혈 되나요?', 20),
      reply(
        'rep-3',
        '김하늘',
        'w-1',
        '네 가능합니다! 병원 헌혈실에서 지정헌혈로 말씀하시면 돼요.',
        12
      ),
    ],
  },
  {
    uuid: 'don-2',
    title: 'A형 전혈 지정헌혈 부탁드립니다',
    content:
      '아버지 항암 치료 중이라 정기적으로 수혈이 필요합니다. 도움 주시면 정말 감사하겠습니다.',
    bloodProduct: 'WHOLE',
    patient: patient('A', 'POSITIVE'),
    writer: writer('이준서', 'w-2', 12),
    images: [],
    hits: 96,
    viewsCount: 96,
    likesCount: 8,
    repliesCount: 3,
    lettersCount: 0,
    liked: false,
    liker: [],
    status: 'ACTIVE',
    createdAt: isoAgo(180),
    lastRenewedAt: isoAgo(180),
    modifiedAt: isoAgo(180),
    renewCount: 0,
    hospitalName: '삼성서울병원',
    hospitalAddress: '서울 강남구 일원로 81',
    region: '서울 강남구',
    deadlineAt: isoIn(24 * 5),
    requiredCount: 2,
    fulfilledCount: 0,
    replies: [
      reply('rep-4', '정민호', 'w-5', '이번 주말에 방문 가능합니다.', 90),
    ],
  },
  {
    uuid: 'don-3',
    title: 'B형 백혈구 헌혈 도와주실 분 찾습니다',
    content:
      '백혈병 치료 중인 동생을 위해 백혈구 헌혈이 필요합니다. 절차는 제가 안내해 드릴게요.',
    bloodProduct: 'LEUKOCYTE',
    patient: patient('B', 'NEGATIVE'),
    writer: writer('박서연', 'w-3', 1),
    images: [],
    hits: 412,
    viewsCount: 412,
    likesCount: 57,
    repliesCount: 21,
    lettersCount: 2,
    liked: true,
    liker: [],
    status: 'ACTIVE',
    createdAt: isoAgo(60 * 26),
    lastRenewedAt: isoAgo(60 * 26),
    modifiedAt: isoAgo(60 * 26),
    renewCount: 1,
    hospitalName: '세브란스병원',
    hospitalAddress: '서울 서대문구 연세로 50-1',
    region: '서울 서대문구',
    deadlineAt: isoIn(24 * 12),
    requiredCount: 4,
    fulfilledCount: 4,
    replies: [
      reply('rep-5', '최유진', 'w-4', '백혈구 헌혈 절차가 궁금해요.', 300),
      reply('rep-6', '박서연', 'w-3', '쪽지로 자세히 안내드릴게요!', 240),
    ],
  },
];

// ===== 커뮤니티(게시판) =====
const BOARDS = [
  {
    uuid: 'brd-1',
    title: '첫 헌혈 후기 남겨요! 생각보다 안 아팠어요',
    content:
      '강남 헌혈의집에서 전혈 헌혈하고 왔습니다. 처음이라 긴장했는데 간호사분이 친절하게 설명해주셔서 편했어요.',
    category: 'BLOOD_DONATION_REVIEW',
    writer: writer('최유진', 'w-4', 5),
    images: [],
    hits: 152,
    likeCount: 24,
    repliesCount: 7,
    liked: false,
    liker: [],
    reportCount: 0,
    createdAt: isoAgo(45),
    modifiedAt: isoAgo(45),
    replies: [
      reply('brep-1', '정민호', 'w-5', '축하드려요! 다음에도 함께해요 :)', 30),
    ],
  },
  {
    uuid: 'brd-2',
    title: '헌혈 전날 먹으면 좋은 음식 있을까요?',
    content:
      '내일 혈소판 헌혈 예정인데 컨디션 관리 팁 있으면 알려주세요. 철분제는 먹어도 되나요?',
    category: 'QNA',
    writer: writer('정민호', 'w-5', 20),
    images: [],
    hits: 88,
    likeCount: 5,
    repliesCount: 14,
    liked: false,
    liker: [],
    reportCount: 0,
    createdAt: isoAgo(300),
    modifiedAt: isoAgo(300),
    replies: [
      reply('brep-2', '김하늘', 'w-1', '전날 물 많이 드시고 푹 주무시면 좋아요.', 120),
      reply('brep-3', '이준서', 'w-2', '철분제는 헌혈 당일엔 피하는 게 좋다고 들었어요.', 90),
    ],
  },
  {
    uuid: 'brd-3',
    title: '강남 헌혈의집 다녀왔습니다 (시설 후기)',
    content:
      '주차 편하고 대기시간도 짧았어요. 간식도 넉넉하게 주셔서 좋았습니다.',
    category: 'BLOOD_HOME_REVIEW',
    bloodHomeName: '헌혈의집 강남센터',
    bloodHomeLocation: '서울 강남구',
    gpa: 4.8,
    writer: writer('김하늘', 'w-1', 3),
    images: [],
    hits: 203,
    likeCount: 18,
    repliesCount: 4,
    liked: false,
    liker: [],
    reportCount: 0,
    createdAt: isoAgo(60 * 20),
    modifiedAt: isoAgo(60 * 20),
    replies: [
      reply('brep-4', '최유진', 'w-4', '저도 여기 자주 가요! 시설 깨끗하죠.', 200),
    ],
  },
  {
    uuid: 'brd-4',
    title: '덕분에 수술 잘 끝났습니다. 정말 감사합니다',
    content:
      '지난주 지정헌혈 도와주신 분들 덕분에 어머니 수술이 무사히 끝났습니다. 이 은혜 잊지 않겠습니다.',
    category: 'FREE',
    writer: writer('박서연', 'w-3', 1),
    images: [],
    hits: 534,
    likeCount: 96,
    repliesCount: 32,
    liked: true,
    liker: [],
    reportCount: 0,
    createdAt: isoAgo(60 * 50),
    modifiedAt: isoAgo(60 * 50),
    replies: [
      reply('brep-5', '이준서', 'w-2', '정말 다행이에요. 쾌차하시길 바랍니다.', 600),
    ],
  },
];

// ===== 공지사항 =====
const NOTICES = [
  {
    uuid: 'ntc-1',
    title: '피플 서비스 점검 안내 (8/20 02:00~04:00)',
    content:
      '더 안정적인 서비스 제공을 위해 시스템 점검을 진행합니다.\n\n점검 시간 동안 앱과 웹 이용이 일시 중단됩니다.\n이용에 불편을 드려 죄송합니다.',
    category: 'MAINTENANCE',
    urgent: true,
    createdAt: isoAgo(60 * 3),
    modifiedAt: isoAgo(60 * 3),
  },
  {
    uuid: 'ntc-2',
    title: '쪽지(채팅) 기능이 새로 추가되었습니다',
    content:
      '헌혈요청 게시글에서 바로 요청자와 대화할 수 있는 쪽지 기능이 추가되었습니다.\n\n헌혈요청 상세 화면의 "채팅으로 도움주기"를 눌러보세요.',
    category: 'UPDATE',
    urgent: false,
    createdAt: isoAgo(60 * 48),
    modifiedAt: isoAgo(60 * 48),
  },
  {
    uuid: 'ntc-3',
    title: '개인정보 처리방침 개정 안내',
    content:
      '2026년 9월 1일부로 개인정보 처리방침이 일부 개정됩니다.\n\n자세한 내용은 본문을 확인해 주세요.',
    category: 'INFO',
    urgent: false,
    createdAt: isoAgo(60 * 24 * 7),
    modifiedAt: isoAgo(60 * 24 * 7),
  },
  {
    uuid: 'ntc-4',
    title: '헌혈 인증 이벤트 — 참여하고 포인트 받아가세요',
    content:
      '8월 한 달간 헌혈 인증을 등록하시면 포인트를 드립니다.\n\n자세한 참여 방법은 이벤트 페이지를 확인해 주세요.',
    category: 'EVENT',
    urgent: false,
    createdAt: isoAgo(60 * 24 * 12),
    modifiedAt: isoAgo(60 * 24 * 12),
  },
];

const delay = <T>(v: T, ms = 150): Promise<T> =>
  new Promise((res) => setTimeout(() => res(v), ms));

/**
 * Spring Page 형태. 서비스(community.ts)는 axios의 data(=Page 객체)를 그대로 반환하고,
 * 훅(fetchDonations)이 다시 { data: result }로 감싸므로 여기서는 Page 객체만 돌려준다.
 */
const asPage = (content: any[]) => ({
  content,
  totalElements: content.length,
  totalPages: 1,
  number: 0,
  size: content.length,
  first: true,
  last: true,
  empty: content.length === 0,
  numberOfElements: content.length,
  pageable: {},
});

/** 상세 사이드바의 모집 현황/참여자 (donationHelper API 더미) */
const HELPERS: Record<string, any> = {
  'don-1': {
    requiredCount: 3,
    fulfilledCount: 2,
    iAmHelping: false,
    helpers: [
      {
        uuid: 'h-1',
        accountUuid: 'w-2',
        displayName: '이준서',
        profileImageUrl: null,
        visitAt: isoIn(20),
        status: 'RESERVED',
        createdAt: isoAgo(30),
      },
      {
        uuid: 'h-2',
        accountUuid: 'w-5',
        displayName: '정민호',
        profileImageUrl: null,
        visitAt: null,
        status: 'PLEDGED',
        createdAt: isoAgo(15),
      },
      {
        uuid: 'h-4',
        accountUuid: 'w-4',
        displayName: '최유진',
        profileImageUrl: null,
        visitAt: isoAgo(60 * 24),
        status: 'DONE',
        createdAt: isoAgo(60 * 30),
      },
    ],
  },
  'don-2': {
    requiredCount: 2,
    fulfilledCount: 1,
    iAmHelping: false,
    helpers: [
      {
        uuid: 'h-3',
        accountUuid: 'w-4',
        displayName: '최유진',
        profileImageUrl: null,
        visitAt: null,
        status: 'DONE',
        createdAt: isoAgo(200),
      },
    ],
  },
};

const emptyProgress = {
  requiredCount: null,
  fulfilledCount: 0,
  iAmHelping: false,
  helpers: [],
};

const contentMock = {
  async getProgress(donationUuid: string) {
    return delay(HELPERS[donationUuid] ?? emptyProgress);
  },
  async help(donationUuid: string) {
    const cur = HELPERS[donationUuid] ?? { ...emptyProgress, helpers: [] };
    if (!cur.iAmHelping) {
      cur.iAmHelping = true;
      cur.fulfilledCount = (cur.fulfilledCount ?? 0) + 1;
      cur.helpers = [
        {
          uuid: 'h-me',
          accountUuid: 'me-uuid',
          displayName: '나',
          profileImageUrl: null,
          visitAt: null,
          status: 'PLEDGED',
          createdAt: new Date().toISOString(),
        },
        ...(cur.helpers ?? []),
      ];
      HELPERS[donationUuid] = cur;
    }
    return delay({ ...cur });
  },
  async cancel(donationUuid: string) {
    const cur = HELPERS[donationUuid];
    if (cur?.iAmHelping) {
      cur.iAmHelping = false;
      cur.fulfilledCount = Math.max(0, (cur.fulfilledCount ?? 1) - 1);
      cur.helpers = (cur.helpers ?? []).filter((h: any) => h.uuid !== 'h-me');
    }
    return delay({ ...(cur ?? emptyProgress) });
  },
  async getDonations() {
    return delay(asPage(DONATIONS));
  },
  async getDonation(id: string) {
    return delay(DONATIONS.find((d) => d.uuid === id) ?? DONATIONS[0]);
  },
  async getBoards() {
    return delay(asPage(BOARDS));
  },
  async getBoard(id: string) {
    return delay(BOARDS.find((b) => b.uuid === id) ?? BOARDS[0]);
  },
  async getNoticeAll() {
    return delay(NOTICES);
  },
};

export default contentMock;
